/* Product buy box (conversion) — variant tiles, purchase options,
   live pricing, media gallery, sticky mobile ATC. Dosing/ingredients
   render inline in the liquid (no dialog, no JS).
   Relies on the theme's <product-form> element (component-product-form.js)
   for the actual add-to-cart request + cart drawer refresh. */

if (typeof customElements.get('product-buybox') === 'undefined') {

  class ProductBuybox extends HTMLElement {

    connectedCallback() {
      const dataEl = this.querySelector('[data-buybox-data]');
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      this.variants = this.data.variants || [];
      this.currency = this.data.currency || 'USD';
      this.currentVariant = this.variants.find(v => v.id === this.data.selectedVariantId) || this.variants.find(v => v.available) || this.variants[0];
      this.selectedPlanId = null; // null = one-time purchase

      this.form = this.querySelector('form');
      this.sellingPlanInput = this.querySelector('[data-buybox-selling-plan]');
      this.priceCurrent = this.querySelector('[data-buybox-price-current]');
      this.priceCompare = this.querySelector('[data-buybox-price-compare]');
      this.priceUnit = this.querySelector('[data-buybox-price-unit]');
      this.atcButton = this.querySelector('[data-buybox-atc]');
      this.stickyBar = this.querySelector('[data-buybox-sticky]');

      this._bindVariantTiles();
      this._bindPurchaseOptions();
      this._bindGallery();
      this._bindAddToCart();
      this._bindStickyBar();
      this._bindConfirmModal();
      this._initAboutDefaultState();

      // Honor the server-checked purchase option (subscribe-default
      // setups) before the first paint
      if (this.querySelector('input[data-buybox-purchase][value="subscription"]:checked')) {
        this.selectedPlanId = this._activePlanId();
      }

      // Reflect initial state (covers ?variant= preselection and OOS defaults)
      this._render();
    }

    /* --- Variant selection --- */

    _bindVariantTiles() {
      this.querySelectorAll('input[data-buybox-variant]').forEach(input => {
        input.addEventListener('change', () => {
          const variant = this.variants.find(v => v.id === Number(input.value));
          if (!variant) return;
          this.currentVariant = variant;
          this._updateUrl();
          this._activateMediaForVariant(variant);
          this._render();
        });
      });
    }

    _updateUrl() {
      if (!window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /* --- Purchase options (one-time vs subscription) --- */

    _bindPurchaseOptions() {
      this.frequencySelect = this.querySelector('[data-buybox-frequency]');

      this.querySelectorAll('input[data-buybox-purchase]').forEach(input => {
        input.addEventListener('change', () => {
          if (input.value === 'subscription') {
            this.selectedPlanId = this._activePlanId();
          } else {
            this.selectedPlanId = null;
          }
          this._render();
        });
      });

      if (this.frequencySelect) {
        this.frequencySelect.addEventListener('change', () => {
          // Choosing a delivery frequency means "subscribe at this
          // frequency" — activate the subscription option if it isn't
          // already, so the selection is never silently dropped.
          this.selectedPlanId = Number(this.frequencySelect.value);
          const subRadio = this.querySelector('input[data-buybox-purchase][value="subscription"]');
          if (subRadio && !subRadio.checked) subRadio.checked = true;
          this._render();
        });
      }

      // Initialize from the server-rendered default (subscription is
      // pre-checked when the variant has selling plans)
      const checkedOption = this.querySelector('input[data-buybox-purchase]:checked');
      if (checkedOption && checkedOption.value === 'subscription') {
        this.selectedPlanId = this._activePlanId();
      }
    }

    _activePlanId() {
      const allocations = this.currentVariant.allocations || [];
      if (!allocations.length) return null;
      if (this.frequencySelect) {
        const chosen = Number(this.frequencySelect.value);
        if (allocations.some(a => a.planId === chosen)) return chosen;
      }
      return allocations[0].planId;
    }

    _currentAllocation() {
      if (this.selectedPlanId === null) return null;
      return (this.currentVariant.allocations || []).find(a => a.planId === this.selectedPlanId) || null;
    }

    /* --- Media gallery --- */

    _bindGallery() {
      this.querySelectorAll('[data-buybox-thumb]').forEach(thumb => {
        thumb.addEventListener('click', () => {
          this._activateMedia(Number(thumb.dataset.mediaId));
        });
      });

      const lightbox = this.querySelector('[data-buybox-lightbox]');
      if (lightbox) {
        this.querySelectorAll('[data-buybox-zoom]').forEach(trigger => {
          trigger.addEventListener('click', () => {
            const active = this.querySelector('.buybox__media.is-active img');
            if (!active) return;
            lightbox.querySelector('img').src = active.currentSrc || active.src;
            lightbox.classList.add('is-open');
            document.documentElement.style.overflow = 'hidden';
          });
        });
        const close = () => {
          lightbox.classList.remove('is-open');
          document.documentElement.style.overflow = '';
        };
        lightbox.querySelector('[data-buybox-lightbox-close]').addEventListener('click', close);
        lightbox.addEventListener('click', e => {
          if (e.target === lightbox) close();
        });
        document.addEventListener('keydown', e => {
          if (e.key === 'Escape' && lightbox.classList.contains('is-open')) close();
        });
      }
    }

    _activateMedia(mediaId) {
      this.querySelectorAll('.buybox__media').forEach(fig => {
        fig.classList.toggle('is-active', Number(fig.dataset.mediaId) === mediaId);
      });
      this.querySelectorAll('[data-buybox-thumb]').forEach(thumb => {
        const active = Number(thumb.dataset.mediaId) === mediaId;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    _activateMediaForVariant(variant) {
      if (variant.mediaId) this._activateMedia(variant.mediaId);
    }

    /* --- Add to cart + cart drawer --- */

    _bindAddToCart() {
      const productForm = this.querySelector('product-form');
      if (productForm) {
        productForm.addEventListener('add-to-cart', () => {
          const sidebar = document.getElementById('site-cart-sidebar');
          if (sidebar && typeof sidebar.show === 'function') {
            sidebar.show();
            return;
          }
          // no drawer: momentary inline confirm on the button that was clicked
          const btn = this.atcButton;
          if (!btn || btn.dataset.confirming) return;
          btn.dataset.confirming = '1';
          const orig = btn.innerHTML;
          btn.textContent = 'Added ✓';
          setTimeout(() => { btn.innerHTML = orig; delete btn.dataset.confirming; }, 1800);
        });
      }
    }

    /* --- Sticky mobile bar --- */

    _bindStickyBar() {
      if (!this.stickyBar || !this.atcButton || !('IntersectionObserver' in window)) return;

      new IntersectionObserver(entries => {
        // Show the bar only once the real ATC button has scrolled out of view
        this.stickyBar.classList.toggle('is-visible', !entries[0].isIntersecting);
      }, { threshold: 0 }).observe(this.atcButton);

      const stickyAtc = this.stickyBar.querySelector('[data-buybox-sticky-atc]');
      if (stickyAtc && this.form) {
        stickyAtc.addEventListener('click', () => {
          if (this._needsSubConfirm()) {
            this._openConfirm();
            return;
          }
          if (typeof this.form.requestSubmit === 'function') {
            this.form.requestSubmit();
          } else {
            this.atcButton.click();
          }
        });
        // Mirror the working/spinner state of the main button
        new MutationObserver(() => {
          stickyAtc.disabled = this.atcButton.disabled || this.atcButton.classList.contains('working');
        }).observe(this.atcButton, { attributes: true, attributeFilter: ['class', 'disabled'] });
      }
    }

    /* --- Subscription confirmation modal --- */

    _needsSubConfirm() {
      return this.confirmModal && this.selectedPlanId !== null && !this._subConfirmed;
    }

    _bindConfirmModal() {
      this.confirmModal = this.querySelector('[data-buybox-confirm]');
      this._subConfirmed = false;
      if (!this.confirmModal || !this.atcButton || !this.form) return;

      // Intercept the button click (fires before the form's submit event,
      // so the theme's ajax handler never runs until confirmed)
      this.atcButton.addEventListener('click', e => {
        if (this._needsSubConfirm()) {
          e.preventDefault();
          this._openConfirm();
        }
      });

      this.confirmModal.querySelector('[data-buybox-confirm-yes]').addEventListener('click', () => {
        this._subConfirmed = true;
        this._closeConfirm();
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          this.atcButton.click();
        }
        // Require confirmation again for the next add
        setTimeout(() => { this._subConfirmed = false; }, 1500);
      });

      this.confirmModal.querySelectorAll('[data-buybox-confirm-no]').forEach(btn => {
        btn.addEventListener('click', () => this._closeConfirm());
      });

      // Switch to one-time: clear the plan selection, then add to cart
      const onetime = this.confirmModal.querySelector('[data-buybox-confirm-onetime]');
      if (onetime) {
        onetime.addEventListener('click', () => {
          const oneTimeRadio = this.querySelector('input[data-buybox-purchase][value="onetime"]');
          if (oneTimeRadio) {
            oneTimeRadio.checked = true;
            oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            this.selectedPlanId = null;
            this._render();
          }
          this._closeConfirm();
          if (typeof this.form.requestSubmit === 'function') {
            this.form.requestSubmit();
          } else {
            this.atcButton.click();
          }
        });
      }
      // Backdrop click (native dialog: a click on the element itself)
      this.confirmModal.addEventListener('click', e => {
        if (e.target === this.confirmModal) this._closeConfirm();
      });
      // Esc closes natively with showModal(); restore focus either way
      this.confirmModal.addEventListener('close', () => {
        if (this._confirmReturnFocus && typeof this._confirmReturnFocus.focus === 'function') {
          this._confirmReturnFocus.focus();
        }
      });
    }

    _openConfirm() {
      this._fillConfirmCopy();
      this._confirmReturnFocus = document.activeElement;
      if (typeof this.confirmModal.showModal === 'function') {
        this.confirmModal.showModal();
      } else {
        this.confirmModal.setAttribute('open', '');
      }
      const yes = this.confirmModal.querySelector('[data-buybox-confirm-yes]');
      if (yes) yes.focus();
    }

    _confirmQty() {
      const qtyEl = this.form ? this.form.querySelector('[name="quantity"]') : null;
      const qty = qtyEl ? parseInt(qtyEl.value, 10) : 1;
      return qty > 0 ? qty : 1;
    }

    /* Recomputed on EVERY open — variant, qty, and frequency may have
       changed. Never cached. */
    _fillConfirmCopy() {
      const v = this.currentVariant;
      const allocation = this._currentAllocation();
      if (!allocation) return;
      const qty = this._confirmQty();

      const thumb = this.confirmModal.querySelector('[data-buybox-confirm-thumb]');
      if (thumb && v.imageUrl) thumb.src = v.imageUrl;

      const freq = this.confirmModal.querySelector('[data-buybox-confirm-freq]');
      if (freq) freq.textContent = 'Delivered every ' + (allocation.interval || '').toLowerCase();

      const saving = this.confirmModal.querySelector('[data-buybox-confirm-saving]');
      if (saving) {
        if (allocation.percent > 0) {
          saving.textContent = 'Saving ' + allocation.percent + '%';
          saving.hidden = false;
        } else {
          saving.hidden = true;
        }
      }

      // CHARGED TODAY: allocation price × qty, one-time total struck through
      const charged = this.confirmModal.querySelector('[data-buybox-confirm-charged]');
      if (charged) {
        const total = allocation.price * qty;
        const compareTotal = v.price * qty;
        charged.textContent = this._formatMoney(total);
        if (qty > 1) {
          const note = document.createElement('span');
          note.className = 'bs-sub-dialog__qty-note';
          note.textContent = ' (' + qty + ' × ' + this._formatMoney(allocation.price) + ')';
          charged.appendChild(note);
        }
        if (compareTotal > total) {
          const compare = document.createElement('span');
          compare.className = 'bs-sub-dialog__compare';
          compare.textContent = this._formatMoney(compareTotal);
          charged.appendChild(compare);
        }
      }

      // EACH RENEWAL: the recurring figure (per-delivery price), not the
      // first-order price — its entire purpose
      const renewal = this.confirmModal.querySelector('[data-buybox-confirm-renewal]');
      if (renewal) {
        const recurring = (allocation.recurring || allocation.price) * qty;
        renewal.textContent = this._formatMoney(recurring) + ' every ' + (allocation.interval || '').toLowerCase();
      }

      // NEXT CHARGE: today + interval, long date
      const next = this.confirmModal.querySelector('[data-buybox-confirm-next]');
      if (next) {
        const schedule = this._intervalSchedule(allocation.interval);
        next.textContent = schedule ? schedule.dateText : (allocation.interval || '—');
      }

      // Switch-to-one-time label carries the one-time total
      const onetimePrice = this.confirmModal.querySelector('[data-buybox-confirm-onetime-price]');
      if (onetimePrice) {
        onetimePrice.textContent = ' — ' + this._formatMoney(v.price * qty);
      }
    }

    /* Parse Appstle plan names like "30 Days" / "2 Weeks" / "1 Month"
       into a real next-payment date */
    _intervalSchedule(interval) {
      const m = /(\d+)\s*(day|week|month)/i.exec(interval || '');
      if (!m) return null;
      const n = parseInt(m[1], 10);
      const unit = m[2].toLowerCase();
      const date = new Date();
      let phrase;
      if (unit === 'day') {
        date.setDate(date.getDate() + n);
        phrase = n + ' days';
      } else if (unit === 'week') {
        date.setDate(date.getDate() + n * 7);
        phrase = n + (n === 1 ? ' week' : ' weeks');
      } else {
        date.setMonth(date.getMonth() + n);
        phrase = n + (n === 1 ? ' month' : ' months');
      }
      let dateText;
      try {
        dateText = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
          month: 'long', day: 'numeric', year: 'numeric'
        }).format(date);
      } catch (e) {
        dateText = date.toDateString();
      }
      return { phrase: phrase, dateText: dateText };
    }

    _closeConfirm() {
      if (typeof this.confirmModal.close === 'function') {
        this.confirmModal.close();
      } else {
        this.confirmModal.removeAttribute('open');
      }
      if (this._confirmReturnFocus && typeof this._confirmReturnFocus.focus === 'function') {
        this._confirmReturnFocus.focus();
      }
    }

    /* --- About details: open on desktop, collapsed on mobile --- */

    _initAboutDefaultState() {
      const about = this.querySelector('[data-buybox-about]');
      if (!about) return;
      if (window.matchMedia('(min-width: 750px)').matches) {
        about.setAttribute('open', '');
      } else {
        about.removeAttribute('open');
      }
    }

    /* --- Rendering --- */

    _formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.currency
        }).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    _renderTiles() {
      // Size tiles mirror the selected purchase mode: subscription
      // shows each variant's plan price (and per-unit), one-time shows
      // the base price.
      const subSelected = this.selectedPlanId !== null;
      this.variants.forEach(v => {
        let cents = v.price;
        if (subSelected) {
          const alloc = (v.allocations || []).find(a => a.planId === this.selectedPlanId) || (v.allocations || [])[0];
          if (alloc) cents = alloc.price;
        }
        const priceEl = this.querySelector('[data-buybox-tile-price="' + v.id + '"]');
        if (priceEl) priceEl.textContent = this._formatMoney(cents);
        const unitEl = this.querySelector('[data-buybox-tile-unit="' + v.id + '"]');
        if (unitEl && v.unitCount > 0) {
          unitEl.textContent = this._formatMoney(Math.round(cents / v.unitCount)) + '/' + (v.unitLabel || 'unit');
        }
      });
    }

    _perUnitText(cents) {
      const v = this.currentVariant;
      if (!v.unitCount || v.unitCount <= 0) return '';
      const per = cents / v.unitCount;
      return this._formatMoney(per) + '/' + (v.unitLabel || 'unit');
    }

    _render() {
      const v = this.currentVariant;
      const allocation = this._currentAllocation();
      const displayedCents = allocation ? allocation.price : v.price;

      // Price block
      if (this.priceCurrent) {
        this.priceCurrent.textContent = allocation ? this._formatMoney(allocation.price) : v.priceFormatted;
      }
      if (this.priceCompare) {
        if (allocation && allocation.price < v.price) {
          this.priceCompare.textContent = v.priceFormatted;
          this.priceCompare.hidden = false;
        } else {
          this.priceCompare.hidden = true;
        }
      }
      if (this.priceUnit) {
        const text = this._perUnitText(displayedCents);
        this.priceUnit.textContent = text;
        this.priceUnit.hidden = !text;
      }

      this._renderTiles();

      // Purchase option card copy
      const oneTimePrice = this.querySelector('[data-buybox-onetime-price]');
      if (oneTimePrice) oneTimePrice.textContent = v.priceFormatted;

      const subPrice = this.querySelector('[data-buybox-sub-price]');
      const subSave = this.querySelector('[data-buybox-sub-save]');
      const previewPlanId = this.selectedPlanId !== null ? this.selectedPlanId : this._previewPlanId();
      const previewAllocation = (v.allocations || []).find(a => a.planId === previewPlanId);
      if (subPrice && previewAllocation) {
        subPrice.textContent = previewAllocation.priceFormatted;
        if (subSave) {
          subSave.textContent = previewAllocation.percent > 0 ? previewAllocation.percent + '%' : '';
        }
      }

      // Selling plan input: disabled when one-time so it isn't submitted
      if (this.sellingPlanInput) {
        if (allocation) {
          this.sellingPlanInput.value = allocation.planId;
          this.sellingPlanInput.disabled = false;
        } else {
          this.sellingPlanInput.value = '';
          this.sellingPlanInput.disabled = true;
        }
      }

      // ATC button state
      if (this.atcButton) {
        const label = this.atcButton.querySelector('.buybox__atc-text');
        if (v.available) {
          this.atcButton.disabled = false;
          if (label) label.textContent = this.atcButton.dataset.labelAdd;
        } else {
          this.atcButton.disabled = true;
          if (label) label.textContent = this.atcButton.dataset.labelSoldOut;
        }
      }

      // Sticky bar
      if (this.stickyBar) {
        const title = this.stickyBar.querySelector('[data-buybox-sticky-variant]');
        if (title) title.textContent = v.title === 'Default Title' ? '' : v.title;
        const price = this.stickyBar.querySelector('[data-buybox-sticky-price]');
        if (price) price.textContent = this._formatMoney(displayedCents);
        const badge = this.stickyBar.querySelector('[data-buybox-sticky-badge]');
        if (badge) {
          if (allocation && allocation.percent > 0) {
            badge.textContent = 'Sub ' + allocation.percent + '% off';
            badge.hidden = false;
          } else {
            badge.hidden = true;
          }
          // .bs-label styling upper-cases it to "SUB X% OFF"
        }
        const stickyAtc = this.stickyBar.querySelector('[data-buybox-sticky-atc]');
        if (stickyAtc) {
          stickyAtc.disabled = !v.available;
          stickyAtc.textContent = v.available ? this.atcButton.dataset.labelAdd : this.atcButton.dataset.labelSoldOut;
        }
      }
    }

    _previewPlanId() {
      // Plan shown inside the (unselected) subscription card
      if (this.frequencySelect) {
        const chosen = Number(this.frequencySelect.value);
        if ((this.currentVariant.allocations || []).some(a => a.planId === chosen)) return chosen;
      }
      const allocations = this.currentVariant.allocations || [];
      return allocations.length ? allocations[0].planId : null;
    }

  }

  customElements.define('product-buybox', ProductBuybox);

}
